import React, { useState } from 'react';

import type { UnitDTO, LessonDTO } from '../StudentDashboardDTOs';
import { convertToCdnUrl } from '@/src/utils';
import lessonVideoThumbnail from '@/src/assets/thumbnails/video-thumbnail-lesson.png';
import strategyVideoThumbnail from '@/src/assets/thumbnails/video-thumbnail-strategy.png';
import { apiRequest } from '@/src/services';
import { Loading } from '@/src/ui';

const checkVideoExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => resolve(true);
        video.onerror = () => resolve(false);
        video.src = convertToCdnUrl(url) ?? '';
    });
};

const VideoPreview: React.FC<{ link: string; type: 'Lesson' | 'Strategy' }> = ({ link, type }) => {
    const [hasError, setHasError] = useState(false);

    const thumbSrc = type === 'Lesson' ? lessonVideoThumbnail : strategyVideoThumbnail;

    if (hasError) return null;

    return (
        <div className="video-container">
            <div className="video-label">{type} Video</div>
            <img
                src={thumbSrc}
                className="video-thumbnail-overlay"
                alt={`${type} Video Thumbnail`}
                onError={(e) => {
                    e.currentTarget.style.display = 'none';
                }}
            />
            <video
                className="preview-video"
                muted
                playsInline
                preload="metadata"
                onError={() => setHasError(true)}
                onMouseOver={(e) => e.currentTarget.play().catch(() => {})}
                onMouseOut={(e) => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                }}
                onClick={() => window.open(link, '_blank')}
            >
                <source
                    src={`${convertToCdnUrl(link)}`}
                    type="video/mp4"
                    onError={() => setHasError(true)}
                />
                Your browser does not support the video tag.
            </video>
        </div>
    );
};

type VideoResource = {
    id: number;
    text: string;
    type: string;
    link: string;
};

const LessonAccordion: React.FC<{
    lesson: LessonDTO;
    isExpanded: boolean;
    onExpand: () => void;
    onCollapse: () => void;
}> = ({ lesson, isExpanded, onExpand, onCollapse }) => {
    const [videos, setVideos] = useState<VideoResource[]>(lesson.subitems || []);
    const [isLoading, setIsLoading] = useState(false);

    const [hasFetched, setHasFetched] = useState(lesson.subitems && lesson.subitems.length > 0);
    const [error, setError] = useState<string | null>(null);

    const handleToggle = async () => {
        if (isExpanded) {
            onCollapse();
            return;
        }

        if (!hasFetched) {
            setIsLoading(true);
            setError(null);
            try {
                const res = await apiRequest<VideoResource[]>(
                    'GET',
                    `lessons/student/lessons/${lesson.id}/videos`
                );
                const rawVideos = res.data || [];

                const validatedVideos = await Promise.all(
                    rawVideos.map(async (v) => {
                        if (!v.link) return null;
                        const isValid = await checkVideoExists(v.link);
                        return isValid ? v : null;
                    })
                );

                const workingVideos = validatedVideos.filter((v): v is VideoResource => v !== null);
                setVideos(workingVideos);
            } catch (err) {
                console.error('Failed to fetch videos', err);
                setError('Failed to load videos.');
            } finally {
                setIsLoading(false);
                setHasFetched(true);
                onExpand();
            }
        } else {
            onExpand();
        }
    };

    const lessonLink =
        videos.find((item) => item.type.toLowerCase().includes('lesson'))?.link || '';
    const strategyLink =
        videos.find((item) => item.type.toLowerCase().includes('strategy'))?.link || '';

    return (
        <div className="lesson-wrapper">
            <div
                className={`lesson-header ${isExpanded ? 'active' : ''}`}
                onClick={handleToggle}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
                <span
                    className="unit-arrow"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        opacity: 0.6,
                        transition: 'transform 0.3s ease',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                >
                    <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M7 8L3 0H12L7 8Z" fill="currentColor" />
                    </svg>
                </span>
                <span>
                    Lesson {lesson.lessonNumber}: {lesson.title}
                </span>
            </div>

            <Loading visible={isLoading} />

            <div className={`lesson-content-wrapper ${isExpanded ? 'open' : ''}`}>
                {error && (
                    <div style={{ padding: '10px 0', color: 'red', fontSize: '0.9em' }}>
                        {error}
                    </div>
                )}

                {hasFetched && !error && videos.length === 0 && (
                    <div className="no-videos-msg">No videos available for this lesson.</div>
                )}

                {hasFetched && videos.length > 0 && (
                    <div className="video-row">
                        {lessonLink ? <VideoPreview link={lessonLink} type="Lesson" /> : null}
                        {strategyLink ? <VideoPreview link={strategyLink} type="Strategy" /> : null}
                    </div>
                )}
            </div>
        </div>
    );
};

const UnitAccordion: React.FC<{
    unit: UnitDTO;
    isExpanded?: boolean;
    onExpand?: () => void;
    onCollapse?: () => void;
}> = ({ unit, isExpanded: externalIsExpanded, onExpand, onCollapse }) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsOpen;

    const [openLessonId, setOpenLessonId] = useState<number | null>(null);

    const [lessons, setLessons] = useState<LessonDTO[]>(unit.lessons || []);
    const [isLoadingLessons, setIsLoadingLessons] = useState(false);

    const [hasFetched, setHasFetched] = useState(unit.lessons && unit.lessons.length > 0);
    const [error, setError] = useState<string | null>(null);

    const handleToggle = async () => {
        if (isExpanded) {
            if (onCollapse) onCollapse();
            else setInternalIsOpen(false);
            return;
        }

        if (!hasFetched) {
            setIsLoadingLessons(true);
            setError(null);
            try {
                const res = await apiRequest<LessonDTO[]>(
                    'GET',
                    `lessons/student/units/${unit.id}/lessons`
                );
                setLessons(res.data || []);
            } catch (err) {
                console.error('Failed to fetch lessons', err);
                setError('Failed to load lessons.');
            } finally {
                setIsLoadingLessons(false);
                setHasFetched(true);
                if (onExpand) onExpand();
                else setInternalIsOpen(true);
            }
        } else {
            if (onExpand) onExpand();
            else setInternalIsOpen(true);
        }
    };

    return (
        <div className="unit-wrapper">
            <div
                className="unit"
                onClick={handleToggle}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
                <span>
                    Unit {unit.unitNumber}: {unit.unitTitle}
                </span>
                <span
                    className="unit-arrow"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        opacity: 0.6,
                        transition: 'transform 0.3s ease',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        marginLeft: 'auto',
                    }}
                >
                    <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M7 8L3 0H12L7 8Z" fill="currentColor" />
                    </svg>
                </span>
            </div>

            <Loading visible={isLoadingLessons} />

            <div
                className={`unit-content-wrapper ${isExpanded ? 'open' : ''}`}
                style={{
                    display: 'grid',
                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                    transition: 'grid-template-rows 0.3s ease-in-out',
                }}
            >
                <div style={{ overflow: 'hidden' }}>
                    <div className="lesson-container">
                        {error && <div style={{ padding: '10px', color: 'red' }}>{error}</div>}

                        {hasFetched && !error && lessons.length === 0 && (
                            <div style={{ padding: '10px', color: '#666' }}>
                                No lessons available in this unit.
                            </div>
                        )}

                        {hasFetched &&
                            lessons.map((lesson) => (
                                <LessonAccordion
                                    key={lesson.id}
                                    lesson={lesson}
                                    isExpanded={openLessonId === lesson.id}
                                    onExpand={() => setOpenLessonId(lesson.id)}
                                    onCollapse={() => setOpenLessonId(null)}
                                />
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnitAccordion;
